import { CopyIcon, KebabHorizontalIcon, LinkIcon, PencilIcon, TrashIcon } from '@primer/octicons-react';
import { ActionList, ActionMenu } from '@primer/react';
import { CellContext } from '@tanstack/react-table';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import { pickPiuLeafProducts } from '@upright/common/model/input/companies/products/product-input-ui/utils';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import ModelledIcon from '@upright/ui/icons/img/streamline-mini-line/interface-essential/hierarchy/interface-hierarchy-3.svg';
import { trackAppcuesEvent } from '@upright/ui/integrations/appcues';
import { AppcuesEventId } from '@upright/ui/integrations/appcues/types';
import { useCallback, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { TableV2 } from '../../../../../common/generic/content/tables/table-v2';
import { ColumnV2 } from '../../../../../common/generic/content/tables/table-v2/types';
import { ModelledAsDialog } from '../modelled-as-dialog';
import { ProductEditDialog } from '../product-edit-dialog';
import { MatchingEditDialog } from '../product-edit-dialog/matching-edit-dialog';
import { ProductMatchingCell, ProductAttributesCell } from './modelling-attributes';
import { ProvidedRevenueCell } from './provided-revenue-cell';
import { RevenueColumnHeader } from './revenue-column-header';
import { computeNormalizedShares } from '../revenue-shares';
import { buildResolvedIprById } from './ipr-inheritance';
import { ResolvedIpr } from '@upright/common/model/input/companies/products/propagate-ipr-attributes';

interface ProductTableProps {
  products: PiuProductState[] | undefined;
  onProductUpdate: (product: PiuProductState) => void;
  duplicateProduct: (product: PiuProductState) => void;
  deleteProduct: (product: PiuProductState) => void;
  /** Show the "Matching" column with the matched product name. Team users only. */
  showMatching?: boolean;
  /** Show the "Attributes" column with the IPR attributes. Enterprise + team users. */
  showAttributes?: boolean;
  disabled: boolean;
  /**
   * PLT-5109: read-only (blanketti) set. Drops the per-product kebab entirely — customer-facing
   * actions (Edit / Duplicate / Delete), the team-only "Internal" (Modelled-as) item, and the
   * per-product edit modal are all hidden.
   */
  readOnly?: boolean;
  companyId?: string;
  /**
   * Segment-editor flat List view (PLT-5707): show a leftmost "Segment" column
   * with the product's ancestor business units as tiles, plus the
   * "Revenue or %" / "Normalized weight" columns, and list only leaf products
   * (segments are surfaced as tiles, not as their own rows). Off for the
   * onboarding / read-only consumers, which keep the plain flat list.
   */
  showSegmentContext?: boolean;
}

export function ProductTable({
  products,
  onProductUpdate,
  showMatching = false,
  showAttributes = false,
  duplicateProduct,
  deleteProduct,
  disabled,
  readOnly = false,
  companyId,
  showSegmentContext = false,
}: ProductTableProps) {
  const isTeam = useIsTeam();

  const [editingProduct, setEditingProduct] = useState<PiuProductState | null>(null);
  const [modelledAsProduct, setModelledAsProduct] = useState<PiuProductState | null>(null);
  // "Edit matching" opens the standalone team-only matching modal (PLT-5707).
  const [matchingProduct, setMatchingProduct] = useState<PiuProductState | null>(null);
  const [, setIsDialogOpen] = useState(false);

  const handleEditProduct = (product: PiuProductState) => {
    setEditingProduct(product);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
  };

  const handleSaveProduct = useCallback(
    (updatedProduct: PiuProductState) => {
      handleCloseDialog();
      onProductUpdate(updatedProduct);
    },
    [onProductUpdate],
  );

  const showAnyExtraColumn = showMatching || showAttributes;

  const allProducts = useMemo(() => products ?? [], [products]);
  // Ancestor-inherited attributes for the product currently open in the edit dialog, so the modal
  // can show them as a read-only hint (PLT-5799). Resolved only while a product is being edited.
  const editingInheritedIpr = useMemo(
    () => (editingProduct ? buildResolvedIprById(allProducts).get(editingProduct.id)?.inheritedIpr : undefined),
    [editingProduct, allProducts],
  );
  const productsById = useMemo(() => new Map(allProducts.map(p => [p.id, p])), [allProducts]);
  // Normalized weights across the whole set (segment-editor list view only).
  const normalizedShares = useMemo(
    () => (showSegmentContext ? computeNormalizedShares(allProducts) : new Map<string, number>()),
    [showSegmentContext, allProducts],
  );
  // Ancestor business units (segments) for a product, root-first, for the tile
  // column. Walks parentId up; a visited-guard keeps a corrupt cycle from
  // looping. Non-segment ancestors (grandfathered product-parents) are skipped.
  const ancestorSegmentTitles = useCallback(
    (product: PiuProductState): string[] => {
      const titles: string[] = [];
      const seen = new Set<string>();
      let current = product.parentId ? productsById.get(product.parentId) : undefined;
      while (current && !seen.has(current.id)) {
        seen.add(current.id);
        if (current.isSegment === true) titles.unshift(current.title || '(untitled)');
        current = current.parentId ? productsById.get(current.parentId) : undefined;
      }
      return titles;
    },
    [productsById],
  );

  // In the segment-editor list view we list only leaf products (segments and any
  // product-parent are surfaced via the Segment tiles, not as their own rows).
  const baseRows = showSegmentContext ? pickPiuLeafProducts(allProducts) : products;

  // Resolve IPR-attribute inheritance across the whole set so the flat List view can show
  // ancestor-derived attributes as "(inherited)" too (PLT-5799) — the Hierarchy view already does
  // this via the resolved tree. Only built when the Attributes column is shown (guarded so the
  // default flat list pays no extra cost).
  const resolvedIprById = useMemo(
    () => (showAttributes ? buildResolvedIprById(allProducts) : new Map<string, ResolvedIpr>()),
    [showAttributes, allProducts],
  );
  // Annotate each row with its resolved / inherited IPR so `ProductAttributesCell` reads the same
  // `resolvedIpr` / `inheritedIpr` shape the hierarchy rows carry. No-op when the Attributes column
  // is off, keeping the flat view byte-identical to before.
  const rows =
    showAttributes && baseRows
      ? baseRows.map(row => {
          const resolved = resolvedIprById.get(row.id);
          return resolved ? { ...row, ...resolved } : row;
        })
      : baseRows;

  const segmentColumn: ColumnV2<PiuProductState> = {
    id: 'segment',
    header: 'Segment',
    enableSorting: false,
    cell: cellProps => {
      const titles = ancestorSegmentTitles(cellProps.row.original);
      // A product with no segment ancestors renders nothing (PLT-5799) — no placeholder dash.
      if (titles.length === 0) return null;
      return (
        <SegmentTiles>
          {titles.map((title, index) => (
            <SegmentTile key={`${index}-${title}`} title={title}>
              {title}
            </SegmentTile>
          ))}
        </SegmentTiles>
      );
    },
    meta: { width: '16em' },
  };

  const providedColumn: ColumnV2<PiuProductState> = {
    id: 'provided',
    header: () => <RevenueColumnHeader />,
    cell: cellProps => (
      <ProvidedRevenueCell
        product={cellProps.row.original}
        editable={false}
        onCommit={() => {
          // Read-only in the list view; revenue is edited in the Hierarchy view's own column.
        }}
      />
    ),
    // Wide enough for the header and its info icon on one line (PLT-7027).
    meta: { width: '10.5em' },
  };

  const normalizedColumn: ColumnV2<PiuProductState> = {
    id: 'normalized',
    header: 'Normalized weight',
    cell: cellProps => (
      <NormalizedValue>{((normalizedShares.get(cellProps.row.original.id) ?? 0) * 100).toFixed(1)}</NormalizedValue>
    ),
    meta: { width: '11em' },
  };

  const baseColumns: Array<ColumnV2<PiuProductState>> = [
    ...(showSegmentContext ? [segmentColumn] : []),
    {
      id: 'title',
      header: 'Product',
      accessorKey: 'title',
      cell: (cellProps: CellContext<PiuProductState, string>) => {
        const product = cellProps.row.original;
        // PLT-5109: on read-only sets show only the product name (which already falls back to the
        // matched taxonomy product title) — never the free-text description, which on legacy sets
        // holds internal analyst notes rather than a customer-facing description. The revenue share
        // is already normalized upstream (canonical blanket weight-resolution) for read-only sets.
        return (
          <ProductTitleCell>
            <TitleWrap>
              <ProductTitle>{product.title}</ProductTitle>
            </TitleWrap>
            {!readOnly && product.description && <ProductDescription>{product.description}</ProductDescription>}
            {/* The dedicated Revenue/Normalized columns replace the inline chip in
                the segment-editor list view (PLT-5707). */}
            {!showSegmentContext && product.revenueShare !== undefined && (
              <RevenueShareChip>{(product.revenueShare * 100).toFixed(1)} % of revenue</RevenueShareChip>
            )}
          </ProductTitleCell>
        );
      },
      meta: {
        // Flexible so the table fills the container instead of leaving dead
        // space on the right when the fixed Segment / revenue columns are shown
        // (PLT-5707); floors at 24em so the name/description stay readable.
        width: showAnyExtraColumn || showSegmentContext ? 'minmax(24em, 1fr)' : 'auto',
      },
    },
  ];

  const matchingColumn: ColumnV2<PiuProductState> = {
    id: 'matching',
    header: 'Matching',
    accessorFn: (row: PiuProductState) => row.productMatchResult?.bestChoice,
    cell: ProductMatchingCell,
    meta: {
      width: 'auto',
    },
  };

  const attributesColumn: ColumnV2<PiuProductState> = {
    id: 'attributes',
    header: 'Attributes',
    accessorFn: (row: PiuProductState) => row.productMatchResult?.bestChoice,
    cell: ProductAttributesCell,
    meta: {
      width: 'auto',
    },
  };

  const actionsColumn: ColumnV2<PiuProductState> = {
    id: 'actions',
    header: '',
    cell: cellProps => {
      const product = cellProps.row.original;
      return (
        <ActionsCellRoot>
          <ActionMenu>
            <ActionMenu.Anchor>
              <button
                type="button"
                onClick={() => {
                  trackAppcuesEvent(AppcuesEventId.productIndividualProductSettingDropDownOpened, {
                    productId: product.id,
                    productTitle: product.title,
                  });
                }}
              >
                <KebabHorizontalIcon size={16} />
              </button>
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <ActionList>
                <ActionList.Item
                  disabled={disabled}
                  onSelect={() => {
                    handleEditProduct(product);
                  }}
                >
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  Edit...
                </ActionList.Item>
                <ActionList.Item
                  disabled={disabled}
                  onSelect={() => {
                    duplicateProduct(product);
                  }}
                >
                  <ActionList.LeadingVisual>
                    <CopyIcon />
                  </ActionList.LeadingVisual>
                  Duplicate
                </ActionList.Item>
                <ActionList.Item
                  disabled={disabled}
                  onSelect={() => {
                    deleteProduct(product);
                  }}
                  variant="danger"
                >
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  Delete
                </ActionList.Item>
                {/* Team-only "Edit matching" in the gated segment-editor list
                    view — matching has its own modal now (PLT-5707). */}
                {isTeam && showSegmentContext && (
                  <ActionList.Item
                    disabled={disabled}
                    onSelect={() => {
                      setMatchingProduct(product);
                    }}
                  >
                    <ActionList.LeadingVisual>
                      <LinkIcon />
                    </ActionList.LeadingVisual>
                    Edit matching
                  </ActionList.Item>
                )}
                {isTeam && (
                  <>
                    <ActionList.Divider />
                    <ActionList.Item
                      onSelect={() => {
                        setModelledAsProduct(product);
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <StyledModelledIcon />
                      </ActionList.LeadingVisual>
                      Internal
                    </ActionList.Item>
                  </>
                )}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ActionsCellRoot>
      );
    },
    enableSorting: false,
    meta: {
      align: 'right',
      width: '4.5em',
    },
  };

  // PLT-5109: on a read-only set the per-product kebab is dropped entirely — including the
  // team-only "Internal" item — so the actions column is omitted.
  const showActionsColumn = !readOnly;

  const columns: Array<ColumnV2<PiuProductState>> = [
    ...baseColumns,
    ...(showSegmentContext ? [providedColumn, normalizedColumn] : []),
    ...(showMatching ? [matchingColumn] : []),
    ...(showAttributes ? [attributesColumn] : []),
    ...(showActionsColumn ? [actionsColumn] : []),
  ];

  if (!products) {
    return <Skeleton width={'100%'} height="300px" />;
  }

  return (
    <>
      <TableV2
        columns={columns}
        data={rows ?? []}
        pageSize={false}
        emptyState={
          <>
            Product list is empty. <b style={{ marginLeft: '0.4em' }}>Add the first product to get started.</b>
          </>
        }
        disablePaginationControls={true}
        getRowProps={_row => ({
          css: css`
            height: auto;
            padding-top: var(--space__lg);
            padding-bottom: var(--space__lg);
          `,
        })}
      />
      {!readOnly && editingProduct && (
        <ProductEditDialog
          mode="edit"
          onClose={handleCloseDialog}
          product={editingProduct}
          onSave={product => {
            handleSaveProduct(product);
          }}
          companyId={companyId}
          // Gated segment-editor list view: matching has its own "Edit matching" modal and revenue
          // belongs to the Hierarchy view's "Revenue or %" column, so the dialog drops both. Name,
          // description and attributes are edited here — the only way to reach them in this view.
          segmentEditorLayout={showSegmentContext}
          inheritedIpr={editingInheritedIpr}
        />
      )}
      {matchingProduct && (
        <MatchingEditDialog
          product={matchingProduct}
          onClose={() => {
            setMatchingProduct(null);
          }}
          onSave={updatedProduct => {
            setMatchingProduct(null);
            onProductUpdate(updatedProduct);
          }}
        />
      )}
      {modelledAsProduct && (
        <ModelledAsDialog
          onClose={() => {
            setModelledAsProduct(null);
          }}
          product={modelledAsProduct}
        />
      )}
    </>
  );
}

const ActionsCellRoot = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: var(--space__sm);
  gap: var(--space__lg);
  align-items: center;
  height: 100%;
`;

const ProductTitleCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__xs);
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__sm);
  line-height: 1;
`;

const ProductTitle = styled.div`
  font-weight: 600;
  font-size: var(--font-size__md);
  color: var(--fgColor-default);
`;

const ProductDescription = styled.div`
  font-size: var(--text-body-size-small);
  color: var(--fgColor-muted);
  line-height: 1.6;
`;

const StyledModelledIcon = styled(ModelledIcon)`
  width: 16px;
  height: 16px;
`;

// The colour was `--platform-green-900`, which is declared nowhere: the whole
// `--platform-*` layer has no green ramp, so the declaration was invalid at
// computed-value time and dropped, and this chip inherited the row's text
// colour instead of being green at all. Repointed at the token of the same
// name rather than at whichever green looked best, so the fix carries no
// design opinion — if a lighter green is wanted here, that is a separate call.
const RevenueShareChip = styled.div`
  border-radius: var(--border-radius__md);
  background-color: white;
  color: var(--color__green-900);
  font-weight: 500;
`;

// Ancestor business-unit tiles shown in the list view's "Segment" column
// (PLT-5707). Multiple ancestors stack; each is truncated with the full name on
// hover (native title tooltip).
const SegmentTiles = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space__xs);
`;

const SegmentTile = styled.span`
  max-width: 100%;
  padding: 2px var(--space__sm);
  border: 1px solid var(--borderColor-default, #d0d7de);
  border-radius: var(--border-radius__md);
  background-color: var(--bgColor-muted, #f6f8fa);
  color: var(--fgColor-default);
  font-size: var(--font-size__sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NormalizedValue = styled.span`
  color: var(--fgColor-muted);
  white-space: nowrap;
`;
